import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Video, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const Analysis = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    if (!file.type.startsWith('video/')) {
      toast.error('Por favor sube un archivo de video');
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      toast.error('El video no puede superar los 500MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          toast.success('¡Video subido! Procesando análisis...');
          setTimeout(() => {
            navigate('/vault');
          }, 2000);
        } else {
          toast.error('Error al subir video');
        }
        setUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Error de red al subir video');
        setUploading(false);
      });

      const token = localStorage.getItem('token');
      xhr.open('POST', `${process.env.REACT_APP_BACKEND_URL}/api/analysis/upload`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    } catch (error) {
      toast.error('Error al subir video');
      setUploading(false);
    }
  }, [navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: uploading,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.webm']
    },
    multiple: false
  });

  return (
    <div className="min-h-screen py-20 px-6 md:px-12 lg:px-24" data-testid="analysis-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-2" data-testid="analysis-title">Subir Material - Análisis IA</h1>
          <p className="text-muted-foreground" data-testid="analysis-subtitle">Subí un video de tu swing para análisis profesional</p>
        </div>

        <Card className="glass-effect border-white/10" data-testid="upload-card">
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <Video className="h-5 w-5" />
              Subir Video de Swing
            </CardTitle>
            <CardDescription>
              El Profesor Virtual PGA analizará tu técnica y te dará recomendaciones personalizadas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
                isDragActive
                  ? 'border-accent bg-accent/10 glow-accent'
                  : 'border-white/20 hover:border-white/40'
              } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="dropzone"
            >
              <input {...getInputProps()} />
              {uploading ? (
                <div className="space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="text-lg font-semibold">Subiendo video...</p>
                  <Progress value={uploadProgress} className="h-2" data-testid="upload-progress" />
                  <p className="text-sm text-muted-foreground">{Math.round(uploadProgress)}%</p>
                </div>
              ) : isDragActive ? (
                <div className="space-y-4">
                  <Upload className="h-12 w-12 text-accent mx-auto" />
                  <p className="text-lg font-semibold text-accent">Soltá el video acá...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
                  <div>
                    <p className="text-lg font-semibold">Arrastrá y soltá tu video</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      o hacé clic para seleccionar (MP4, MOV, AVI, WebM - Máx 500MB)
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-sm">Consejos para mejor análisis</p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Grabá desde un ángulo lateral o frontal</li>
                    <li>• Asegurate de que el swing completo sea visible</li>
                    <li>• Buena iluminación mejora la precisión</li>
                    <li>• Evitá fondos muy desordenados</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="glass-effect border-white/10" data-testid="feature-strengths">
            <CardContent className="pt-6">
              <CheckCircle className="h-8 w-8 text-accent mb-3" />
              <h3 className="font-semibold mb-2">Fortalezas</h3>
              <p className="text-sm text-muted-foreground">
                Identificamos aspectos técnicos que estás ejecutando correctamente
              </p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-white/10" data-testid="feature-weaknesses">
            <CardContent className="pt-6">
              <AlertCircle className="h-8 w-8 text-accent mb-3" />
              <h3 className="font-semibold mb-2">Debilidades</h3>
              <p className="text-sm text-muted-foreground">
                Detectamos fallas comunes y áreas de mejora en tu swing
              </p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-white/10" data-testid="feature-drills">
            <CardContent className="pt-6">
              <Video className="h-8 w-8 text-accent mb-3" />
              <h3 className="font-semibold mb-2">Ejercicios</h3>
              <p className="text-sm text-muted-foreground">
                Recibí 3 ejercicios específicos para mejorar tu juego
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};